/**
 * <GameOverShareRow>: item 9.
 *
 * The game-over share affordance, rendered below the Rematch / Leave buttons as
 * a SINGLE centered horizontal line:
 *
 *     Room Code: AB2C   [ Share Room Link ]
 *
 * It replaces the generic <ShareLinkCTA> (a vertical code + URL + two buttons)
 * in the game-over context only, the lobby + empty-room nudge keep ShareLinkCTA.
 * The one button shares the room link: it prefers the native share sheet and
 * falls back to copying the link to the clipboard (with a transient "copied"
 * confirmation).
 */

import { useState } from "preact/hooks";

type GameOverShareRowProps = {
  roomId: string;
};

export function GameOverShareRow({ roomId }: GameOverShareRowProps) {
  const [copied, setCopied] = useState(false);

  const roomUrl = `${window.location.origin}/r/${roomId}`;

  const copyLink = async () => {
    // Only surface the "copied" confirmation when the copy ACTUALLY succeeded,
    // a false "✓ Link copied!" when nothing was copied is worse than no feedback.
    let ok = false;
    try {
      await navigator.clipboard.writeText(roomUrl);
      ok = true;
    } catch {
      // Fallback for browsers without the async clipboard API.
      const el = document.createElement("textarea");
      el.value = roomUrl;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      try {
        ok = document.execCommand("copy");
      } catch {
        ok = false;
      }
      document.body.removeChild(el);
    }
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    // Prefer the native share sheet (mobile); fall back to clipboard copy.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "Join my drawlol room!", url: roomUrl });
        return;
      } catch {
        // User cancelled or the share failed, fall through to copy.
      }
    }
    await copyLink();
  };

  return (
    <div class="game-over-share-row" data-testid="game-over-share-row">
      <span class="game-over-room-code">
        Room code: <strong>{roomId}</strong>
      </span>
      <button
        type="button"
        class="game-over-share-btn"
        onClick={() => void handleShare()}
        // Drive the accessible name off `copied` so a screen reader hears the
        // confirmation: an aria-label otherwise overrides the visible text swap,
        // leaving SR users with no feedback (matches ShareLinkCTA's copy button).
        aria-label={copied ? "Copied!" : "Share room link"}
      >
        {copied ? "✓ Copied!" : "Share room link"}
      </button>
    </div>
  );
}
